import { type QrResponseDto } from "@/api";
import { downloadFile } from "@/lib/utils/downloadUtils";
import { DownloadIcon } from "lucide-react";
import { Button } from "@photo-booth/ui/button";
import { useNavigate } from "react-router";
import { useEffect, useMemo } from "react";
import { useCountdown } from "usehooks-ts";
import { PRICING } from "@/lib/constants/pricing";
import { useFrameStore } from "@/lib/store/frameStore";

interface QrContentProps {
  qr: QrResponseDto | null;
  error: string | null;
}

export function QrContent({ qr, error }: QrContentProps) {
  const navigate = useNavigate();
  const { frame } = useFrameStore();
  const priceInBaht = frame?.price ? Math.round(frame.price / 100) : PRICING.DEFAULT_AMOUNT;
  const timeRemaining = qr?.expiry
    ? new Date(qr.expiry).getTime() - new Date().getTime()
    : 0;
  const [countDown, { startCountdown }] = useCountdown({
    countStart: Math.round(timeRemaining / 1000),
    countStop: 0,
    intervalMs: 1000,
    isIncrement: false,
  });
  const formattedTimeRemaining = useMemo(() => {
    const minutes = Math.floor(countDown / 60);
    const seconds = countDown % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, [countDown]);

  useEffect(() => {
    startCountdown();
  }, [startCountdown]);

  useEffect(() => {
    if (countDown === 0) {
      const timeoutId = setTimeout(() => {
        navigate("/frame-detail");
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [countDown, navigate]);

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  const handleDownload = () => {
    downloadFile(qr?.qrCodeDataUrl || "", "qr.png");
  };

  if (!qr || !qr.qrCodeDataUrl) {
    return (
      <div className="text-red-500 flex flex-col items-center justify-center">
        QR code not found
        <Button
          variant="outline"
          className="text-vintage-black mt-4"
          onClick={() => navigate("/frame-detail")}
        >
          {" "}
          please select a frame again{" "}
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col items-center w-full max-w-md mx-auto">
        <div className=" border-8 border-vintage-red bg-vintage-red px-2 pt-2 rounded-xl flex flex-col items-center justify-center">
          <img
            src={qr.qrCodeDataUrl || ""}
            alt="QR Code"
            className="w-full max-w-[280px] h-auto rounded-lg"
          />

          {/* Timer and Download Button */}
          <div className="text-right self-end mt-2 pe-2 flex items-center gap-2 justify-end">
            <p className="font-family-reenie text-natural-white text-[30px]">
              {priceInBaht} bath
            </p>
          </div>
        </div>
          <p className="mt-4 text-vintage-black text-lg font-medium">
            {formattedTimeRemaining}
          </p>
        <Button
          variant="outline"
          onClick={handleDownload}
          className="mt-4 border-vintage-red text-vintage-red"
        >
          <DownloadIcon className="w-4 h-4" />
          Download QR Code
        </Button>
      </div>
    </>
  );
}
